import React, { useState, useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navigation from '../components/Navigation';
import { getAuthHeaders } from '../lib/auth';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Sparkles, Upload, Crown, TrendingUp, Target, Share2, Image as ImageIcon,
  Type, Square, Circle, Download, Trash2, Copy, Layers, Plus, ChevronRight,
  ChevronLeft, AlignLeft, AlignCenter, AlignRight, Bold, X, Undo, Redo,
  Search, ArrowUp, ArrowDown, Eye, EyeOff, RotateCcw
} from 'lucide-react';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  'Realtor', 'Restaurant', 'Retail Store', 'Salon/Spa', 'Fitness Center',
  'Tech Company', 'Consulting', 'Healthcare', 'Education', 'Legal Services',
  'Construction', 'Photography', 'Event Planning', 'Marketing Agency',
  'Financial Services', 'Automotive', 'Home Services', 'E-commerce', 'Other'
];

const CANVAS_SIZES = {
  instagram_square: { width: 1080, height: 1080, label: 'Instagram Square (1080x1080)' },
  instagram_story: { width: 1080, height: 1920, label: 'Instagram Story (1080x1920)' },
  facebook_post: { width: 1200, height: 630, label: 'Facebook Post (1200x630)' },
  poster: { width: 1080, height: 1350, label: 'Poster (1080x1350)' }
};

const GOOGLE_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 'Raleway',
  'Poppins', 'Merriweather', 'Playfair Display', 'Source Sans Pro', 'Ubuntu',
  'Nunito', 'PT Sans', 'Mukta', 'Rubik', 'Work Sans', 'Bebas Neue', 'Dancing Script',
  'Pacifico', 'Lobster', 'Righteous', 'Anton', 'Archivo', 'Barlow', 'Caveat',
  'Cinzel', 'Comfortaa', 'DM Sans', 'EB Garamond', 'Fira Sans', 'Great Vibes',
  'Indie Flower', 'Josefin Sans', 'Karla', 'Libre Baskerville', 'Manrope',
  'Noto Sans', 'Oxygen', 'PT Serif', 'Quicksand', 'Roboto Condensed', 'Roboto Mono',
  'Shadows Into Light', 'Signika', 'Titillium Web', 'Varela Round', 'Yanone Kaffeesatz'
];

const TEMPLATE_CATEGORIES = [
  {
    id: 'birthday',
    name: 'Birthday',
    searchQuery: 'birthday celebration',
    template: {
      elements: [
        { type: 'text', x: 100, y: 150, w: 880, h: 120, zIndex: 2, props: { text: 'Happy Birthday!', fontSize: 72, fontFamily: 'Pacifico', color: '#ff1493', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 300, w: 880, h: 80, zIndex: 2, props: { text: 'Wishing you a wonderful day!', fontSize: 32, fontFamily: 'Dancing Script', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#ff69b4', backgroundOpacity: 0.8 } }
      ]
    }
  },
  {
    id: 'anniversary',
    name: 'Anniversary',
    searchQuery: 'wedding anniversary',
    template: {
      elements: [
        { type: 'text', x: 100, y: 200, w: 880, h: 100, zIndex: 2, props: { text: 'Happy Anniversary', fontSize: 64, fontFamily: 'Great Vibes', color: '#8b4513', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 330, w: 880, h: 70, zIndex: 2, props: { text: 'Celebrating Your Love', fontSize: 36, fontFamily: 'Playfair Display', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#d4af37', backgroundOpacity: 0.9 } }
      ]
    }
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    searchQuery: 'thanksgiving dinner',
    template: {
      elements: [
        { type: 'text', x: 100, y: 180, w: 880, h: 110, zIndex: 2, props: { text: 'Happy Thanksgiving', fontSize: 68, fontFamily: 'Lobster', color: '#ff8c00', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 320, w: 880, h: 75, zIndex: 2, props: { text: 'Grateful for You', fontSize: 38, fontFamily: 'Merriweather', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#8b4513', backgroundOpacity: 0.85 } }
      ]
    }
  },
  {
    id: 'business',
    name: 'Business',
    searchQuery: 'business professional',
    template: {
      elements: [
        { type: 'text', x: 100, y: 250, w: 880, h: 90, zIndex: 2, props: { text: 'Your Business Name', fontSize: 58, fontFamily: 'Montserrat', color: '#1e3a8a', fontWeight: 'bold', textAlign: 'center' } },
        { type: 'text', x: 100, y: 370, w: 880, h: 65, zIndex: 2, props: { text: 'Professional Services', fontSize: 32, fontFamily: 'Inter', color: '#ffffff', fontWeight: 'normal', textAlign: 'center', backgroundColor: '#3b82f6', backgroundOpacity: 0.9 } }
      ]
    }
  },
  {
    id: 'social',
    name: 'Social Media',
    searchQuery: 'social media colorful',
    template: {
      elements: [
        { type: 'text', x: 100, y: 220, w: 880, h: 100, zIndex: 2, props: { text: 'Follow Us!', fontSize: 70, fontFamily: 'Bebas Neue', color: '#ffffff', fontWeight: 'bold', textAlign: 'center', backgroundColor: '#8b5cf6', backgroundOpacity: 0.85 } },
        { type: 'text', x: 100, y: 350, w: 880, h: 70, zIndex: 2, props: { text: '@YourHandle', fontSize: 42, fontFamily: 'Roboto', color: '#fbbf24', fontWeight: 'bold', textAlign: 'center' } }
      ]
    }
  }
];

// Load Google Fonts dynamically
const loadGoogleFont = (fontName) => {
  const formattedName = fontName.replace(/ /g, '+');
  const link = document.createElement('link');
  link.href = `https://fonts.googleapis.com/css2?family=${formattedName}:wght@400;600;700&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

export default function DigiStrategyWithEditor() {
  // Profile & Strategy States
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [generationCount, setGenerationCount] = useState(0);
  const [isPro, setIsPro] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  
  const [form, setForm] = useState({
    businessName: '',
    ownerName: '',
    businessType: 'Realtor',
    businessEmail: '',
    businessLocation: '',
    businessPhone: '',
    businessDescription: '',
    logoUrl: ''
  });

  // Editor States
  const canvasRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState('instagram_square');
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showStockImages, setShowStockImages] = useState(false);
  const [stockImages, setStockImages] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);
  const [currentDesignId, setCurrentDesignId] = useState(null);
  const [showMyDesigns, setShowMyDesigns] = useState(false);
  const [myDesigns, setMyDesigns] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [removingBackground, setRemovingBackground] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(null);
  const [showBackgroundOptions, setShowBackgroundOptions] = useState(false);

  useEffect(() => {
    loadProfile();
    // Load popular Google Fonts on mount
    ['Inter', 'Roboto', 'Open Sans', 'Montserrat', 'Poppins'].forEach(font => loadGoogleFont(font));
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/strategy/profile`, {
        headers: getAuthHeaders()
      });
      
      if (response.data.exists) {
        setHasProfile(true);
        const profile = response.data.profile;
        setForm({
          businessName: profile.businessName || '',
          ownerName: profile.ownerName || '',
          businessType: profile.businessType || 'Realtor',
          businessEmail: profile.businessEmail || '',
          businessLocation: profile.businessLocation || '',
          businessPhone: profile.businessPhone || '',
          businessDescription: profile.businessDescription || '',
          logoUrl: profile.logoUrl || ''
        });
      }
      
      const genResponse = await axios.get(`${API}/strategy/generations`, {
        headers: getAuthHeaders()
      });
      
      if (genResponse.data.generations && genResponse.data.generations.length > 0) {
        const latest = genResponse.data.generations[genResponse.data.generations.length - 1];
        setStrategy(latest);
        setGenerationCount(latest.generationCount);
        setIsPro(latest.isPro || false);
      }
      
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!form.businessName || !form.ownerName || !form.businessEmail || !form.businessDescription) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setGenerating(true);
      const response = await axios.post(
        `${API}/strategy/generate`,
        form,
        { headers: getAuthHeaders() }
      );

      setStrategy(response.data);
      setGenerationCount(response.data.generationCount);
      setHasProfile(true);
      
      toast.success(response.data.message || 'Strategy generated successfully!');
      
      if (response.data.remainingFreeGenerations === 0 && !isPro) {
        toast.warning('You have used all free generations. Upgrade to Pro for unlimited!');
      }
      
    } catch (error) {
      console.error('Failed to generate:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Generation limit reached. Please upgrade to Pro.');
      } else {
        toast.error('Failed to generate strategy');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleUpgradeToPro = async () => {
    try {
      await axios.post(`${API}/strategy/upgrade-pro`, {}, { headers: getAuthHeaders() });
      setIsPro(true);
      toast.success('Upgraded to Pro! Unlimited strategy generations unlocked.');
    } catch (error) {
      console.error('Failed to upgrade:', error);
      toast.error('Failed to upgrade to Pro');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, logoUrl: reader.result });
        toast.success('Logo uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  // ============= EDITOR FUNCTIONS =============

  const openEditor = async (post = null) => {
    setSelectedPost(post);
    setShowEditor(true);
    
    // Load stock image from Pexels based on business type
    toast.info('Loading background image...');
    
    try {
      const searchQuery = form.businessType || 'business';
      const pexelsResponse = await axios.get(
        `https://api.pexels.com/v1/search?query=${searchQuery}&per_page=1`,
        {
          headers: {
            Authorization: 'wqs31dEukjAHuUM7ARtAcIsEGVWUus6cQ4ZaDbTOkXCsGFJNuPFg2RLJ'
          }
        }
      );
      
      if (pexelsResponse.data.photos && pexelsResponse.data.photos.length > 0) {
        const imageUrl = pexelsResponse.data.photos[0].src.large;
        toast.success('Background image loaded!');
        initializeCanvas(post, imageUrl);
      } else {
        initializeCanvas(post, null);
      }
    } catch (error) {
      console.error('Failed to load image:', error);
      toast.error('Failed to load background image');
      initializeCanvas(post, null);
    }
  };

  const closeEditor = async () => {
    // Auto-save before closing
    await saveDesign();
    setShowEditor(false);
    setSelectedPost(null);
    setElements([]);
    setSelectedElement(null);
    setCurrentDesignId(null);
  };
  
  const saveDesign = async () => {
    try {
      const designData = {
        elements,
        canvasSize,
        backgroundColor,
        backgroundImage,
        businessInfo: form,
        postContent: selectedPost?.content || ''
      };
      
      if (currentDesignId) {
        // Update existing design
        await axios.put(
          `${API}/strategy/designs/${currentDesignId}`,
          designData,
          { headers: getAuthHeaders() }
        );
        toast.success('Design saved!');
      } else {
        // Create new design
        const response = await axios.post(
          `${API}/strategy/designs`,
          designData,
          { headers: getAuthHeaders() }
        );
        setCurrentDesignId(response.data.designId);
        toast.success('Design saved!');
      }
    } catch (error) {
      console.error('Failed to save design:', error);
      toast.error('Failed to save design');
    }
  };
  
  const loadMyDesigns = async () => {
    try {
      const response = await axios.get(`${API}/strategy/designs`, {
        headers: getAuthHeaders()
      });
      setMyDesigns(response.data.designs || []);
      setShowMyDesigns(true);
    } catch (error) {
      console.error('Failed to load designs:', error);
      toast.error('Failed to load designs');
    }
  };
  
  const loadDesign = (design) => {
    setElements(design.elements || []);
    setCanvasSize(design.canvasSize || 'instagram_square');
    setBackgroundColor(design.backgroundColor || '#ffffff');
    setBackgroundImage(design.backgroundImage || '');
    setCurrentDesignId(design.id);
    setShowMyDesigns(false);
    setShowEditor(true);
    toast.success('Design loaded!');
  };
  
  const deleteDesign = async (designId) => {
    try {
      await axios.delete(`${API}/strategy/designs/${designId}`, {
        headers: getAuthHeaders()
      });
      setMyDesigns(myDesigns.filter(d => d.id !== designId));
      toast.success('Design deleted!');
    } catch (error) {
      console.error('Failed to delete design:', error);
      toast.error('Failed to delete design');
    }
  };
  
  const loadTemplate = async (template) => {
    setShowTemplates(false);
    setShowEditor(true);
    toast.info('Loading template...');
    
    try {
      // Get background image from Pexels
      const pexelsResponse = await axios.get(
        `https://api.pexels.com/v1/search?query=${template.searchQuery}&per_page=1`,
        {
          headers: {
            Authorization: 'wqs31dEukjAHuUM7ARtAcIsEGVWUus6cQ4ZaDbTOkXCsGFJNuPFg2RLJ'
          }
        }
      );
      
      if (pexelsResponse.data.photos && pexelsResponse.data.photos.length > 0) {
        setBackgroundImage(pexelsResponse.data.photos[0].src.large);
      }
      
      // Load template elements
      setElements(JSON.parse(JSON.stringify(template.template.elements)));
      saveToHistory(template.template.elements);
      toast.success('Template loaded!');
    } catch (error) {
      console.error('Failed to load template:', error);
      setElements(JSON.parse(JSON.stringify(template.template.elements)));
      saveToHistory(template.template.elements);
      toast.warning('Template loaded without background image');
    }
  };

  const initializeCanvas = (post, generatedImageUrl = null) => {
    const size = CANVAS_SIZES[canvasSize];
    const newElements = [];
    
    // Set background
    if (generatedImageUrl) {
      setBackgroundImage(generatedImageUrl);
      setBackgroundColor('#ffffff');
    } else {
      setBackgroundColor('#f8f9fa');
      setBackgroundImage('');
    }
    
    // Business Logo (moved to bottom with info)
    // Removed from top - will be added with contact info below
    
    // Post Content
    if (post?.content) {
      newElements.push({
        id: Date.now() + 2,
        type: 'text',
        x: 50,
        y: size.height / 2 - 150,
        w: size.width - 100,
        h: 250,
        zIndex: 3,
        props: {
          text: post.content,
          fontSize: 28,
          fontFamily: 'Inter',
          color: '#374151',
          fontWeight: 'normal',
          textAlign: 'left'
        }
      });
    }
    
    // Beautiful Wave Background for Contact Section
    const contactY = size.height - 280;
    
    // Bottom decorative wave shape (dark brown/maroon)
    newElements.push({
      id: Date.now() + 3,
      type: 'rectangle',
      x: 0,
      y: contactY + 80,
      w: size.width,
      h: size.height - (contactY + 80),
      zIndex: 1,
      props: {
        color: '#5c3d2e',
        opacity: 1,
        borderRadius: 0
      }
    });
    
    // Top decorative wave shape (orange to yellow gradient) - simulated with rectangle
    newElements.push({
      id: Date.now() + 4,
      type: 'rectangle',
      x: 0,
      y: contactY + 50,
      w: size.width,
      h: 60,
      zIndex: 2,
      props: {
        color: '#ff6b35',
        opacity: 0.9,
        borderRadius: 0
      }
    });
    
    // Logo with contact info (moved from top)
    if (form.logoUrl) {
      newElements.push({
        id: Date.now() + 5,
        type: 'image',
        x: 50,
        y: contactY + 60,
        w: 80,
        h: 80,
        zIndex: 4,
        props: { 
          src: form.logoUrl, 
          opacity: 1,
          borderRadius: 40, // Circle by default
          logoShape: 'circle' // New property to track shape
        }
      });
    }
    
    // Business Name with contact info
    newElements.push({
      id: Date.now() + 6,
      type: 'text',
      x: form.logoUrl ? 150 : 50,
      y: contactY + 60,
      w: size.width - (form.logoUrl ? 200 : 100),
      h: 35,
      zIndex: 4,
      props: {
        text: form.businessName || 'Your Business Name',
        fontSize: 28,
        fontFamily: 'Inter',
        color: '#ffffff',
        fontWeight: 'bold',
        textAlign: 'left',
        backgroundColor: 'transparent',
        backgroundOpacity: 1
      }
    });
    
    // Owner Name
    newElements.push({
      id: Date.now() + 7,
      type: 'text',
      x: form.logoUrl ? 150 : 50,
      y: contactY + 100,
      w: size.width - (form.logoUrl ? 200 : 100),
      h: 25,
      zIndex: 4,
      props: {
        text: form.ownerName || 'Contact Name',
        fontSize: 18,
        fontFamily: 'Inter',
        color: '#ffffff',
        fontWeight: 'normal',
        textAlign: 'left',
        backgroundColor: 'transparent',
        backgroundOpacity: 1
      }
    });
    
    // Location
    newElements.push({
      id: Date.now() + 8,
      type: 'text',
      x: form.logoUrl ? 150 : 50,
      y: contactY + 130,
      w: size.width - (form.logoUrl ? 200 : 100),
      h: 25,
      zIndex: 4,
      props: {
        text: `📍 ${form.businessLocation || 'Location'}`,
        fontSize: 16,
        fontFamily: 'Inter',
        color: '#ffffff',
        fontWeight: 'normal',
        textAlign: 'left',
        backgroundColor: 'transparent',
        backgroundOpacity: 1
      }
    });
    
    // Email
    newElements.push({
      id: Date.now() + 9,
      type: 'text',
      x: form.logoUrl ? 150 : 50,
      y: contactY + 155,
      w: size.width - (form.logoUrl ? 200 : 100),
      h: 25,
      zIndex: 4,
      props: {
        text: `✉️ ${form.businessEmail || 'email@business.com'}`,
        fontSize: 16,
        fontFamily: 'Inter',
        color: '#ffffff',
        fontWeight: 'normal',
        textAlign: 'left',
        backgroundColor: 'transparent',
        backgroundOpacity: 1
      }
    });
    
    // Phone
    if (form.businessPhone) {
      newElements.push({
        id: Date.now() + 10,
        type: 'text',
        x: form.logoUrl ? 150 : 50,
        y: contactY + 180,
        w: size.width - (form.logoUrl ? 200 : 100),
        h: 25,
        zIndex: 4,
        props: {
          text: `📞 ${form.businessPhone}`,
          fontSize: 16,
          fontFamily: 'Inter',
          color: '#ffffff',
          fontWeight: 'normal',
          textAlign: 'left',
          backgroundColor: 'transparent',
          backgroundOpacity: 1
        }
      });
    }
    
    setElements(newElements);
    saveToHistory(newElements);
  };

  const addTextElement = (withBackground = false) => {
    const size = CANVAS_SIZES[canvasSize];
    const newElement = {
      id: Date.now(),
      type: 'text',
      x: 100,
      y: 100,
      w: 300,
      h: 60,
      zIndex: elements.length + 1,
      props: {
        text: 'Double click to edit',
        fontSize: 24,
        fontFamily: 'Inter',
        color: '#000000',
        fontWeight: 'normal',
        textAlign: 'left',
        backgroundColor: withBackground ? '#ffffff' : 'transparent',
        backgroundOpacity: 0.8
      }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setSelectedElement(newElement.id);
    toast.success('Text element added');
  };

  const addShape = (shape) => {
    const newElement = {
      id: Date.now(),
      type: shape,
      x: 100,
      y: 100,
      w: 200,
      h: 200,
      zIndex: elements.length + 1,
      props: {
        color: '#3b82f6',
        opacity: 1,
        borderRadius: shape === 'rectangle' ? 8 : 0
      }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setSelectedElement(newElement.id);
    toast.success(`${shape} added`);
  };

  const saveToHistory = (newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newElements)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex - 1])));
      toast.success('Undo');
    } else {
      toast.error('Nothing to undo');
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setElements(JSON.parse(JSON.stringify(history[historyIndex + 1])));
      toast.success('Redo');
    } else {
      toast.error('Nothing to redo');
    }
  };

  const updateElement = (id, updates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, ...updates } : el
    );
    setElements(newElements);
    saveToHistory(newElements);
  };

  const updateElementProps = (id, propUpdates) => {
    const newElements = elements.map(el => 
      el.id === id ? { ...el, props: { ...el.props, ...propUpdates } } : el
    );
    setElements(newElements);
    saveToHistory(newElements);
  };

  const deleteElement = (id) => {
    const newElements = elements.filter(el => el.id !== id);
    setElements(newElements);
    saveToHistory(newElements);
    if (selectedElement === id) setSelectedElement(null);
    toast.success('Element deleted');
  };

  const duplicateElement = (id) => {
    const element = elements.find(el => el.id === id);
    if (element) {
      const newElement = {
        ...element,
        id: Date.now(),
        x: element.x + 20,
        y: element.y + 20
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      saveToHistory(newElements);
      toast.success('Element duplicated');
    }
  };

  const moveLayerUp = (id) => {
    const index = elements.findIndex(el => el.id === id);
    if (index < elements.length - 1) {
      const newElements = [...elements];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      // Update z-indexes
      newElements.forEach((el, i) => el.zIndex = i);
      setElements(newElements);
      saveToHistory(newElements);
      toast.success('Layer moved up');
    }
  };

  const moveLayerDown = (id) => {
    const index = elements.findIndex(el => el.id === id);
    if (index > 0) {
      const newElements = [...elements];
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
      // Update z-indexes
      newElements.forEach((el, i) => el.zIndex = i);
      setElements(newElements);
      saveToHistory(newElements);
      toast.success('Layer moved down');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Ask for background removal
      const removeBackground = window.confirm('Remove background from this image?');
      
      if (removeBackground) {
        setRemovingBackground(true);
        toast.info('Removing background...');
        
        try {
          // Use remove.bg API (free tier: 50 images/month)
          const formData = new FormData();
          formData.append('image_file', file);
          formData.append('size', 'auto');
          
          const response = await axios.post(
            'https://api.remove.bg/v1.0/removebg',
            formData,
            {
              headers: {
                'X-Api-Key': 'demo-key', // Using demo for testing - replace with actual key
              },
              responseType: 'blob'
            }
          );
          
          // Convert blob to base64
          const reader = new FileReader();
          reader.onloadend = () => {
            addImageToCanvas(reader.result);
            setRemovingBackground(false);
          };
          reader.readAsDataURL(response.data);
        } catch (error) {
          console.error('Background removal failed:', error);
          toast.error('Background removal failed. Using original image.');
          setRemovingBackground(false);
          
          // Fallback to original image
          const reader = new FileReader();
          reader.onloadend = () => addImageToCanvas(reader.result);
          reader.readAsDataURL(file);
        }
      } else {
        // No background removal
        const reader = new FileReader();
        reader.onloadend = () => addImageToCanvas(reader.result);
        reader.readAsDataURL(file);
      }
    }
  };
  
  const addImageToCanvas = (imageSrc) => {
    const newElement = {
      id: Date.now(),
      type: 'image',
      x: 100,
      y: 100,
      w: 300,
      h: 300,
      zIndex: elements.length + 1,
      props: { src: imageSrc, opacity: 1 }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    toast.success('Image uploaded');
  };

  const searchStockImages = async (query = null) => {
    setLoadingStock(true);
    const searchQuery = query || form.businessType || 'business';
    
    try {
      // Get multiple images from Pexels only
      const pexelsResponse = await axios.get(
        `https://api.pexels.com/v1/search?query=${searchQuery}&per_page=30`,
        {
          headers: {
            Authorization: 'wqs31dEukjAHuUM7ARtAcIsEGVWUus6cQ4ZaDbTOkXCsGFJNuPFg2RLJ'
          }
        }
      );
      
      if (pexelsResponse.data.photos && pexelsResponse.data.photos.length > 0) {
        setStockImages(pexelsResponse.data.photos.map(p => ({
          id: p.id,
          url: p.src.large,
          thumbnail: p.src.medium,
          photographer: p.photographer
        })));
        setShowStockImages(true);
        toast.success(`Found ${pexelsResponse.data.photos.length} images`);
      } else {
        toast.error('No images found. Try a different search term.');
      }
    } catch (error) {
      console.error('Failed to load stock images:', error);
      toast.error('Failed to load stock images. Please try again.');
    } finally {
      setLoadingStock(false);
    }
  };

  const addStockImageToCanvas = (imageUrl) => {
    // If replacing an existing image
    if (showImageOptions) {
      updateElementProps(showImageOptions, { src: imageUrl });
      setShowImageOptions(null);
      setShowStockImages(false);
      toast.success('Image replaced!');
      return;
    }
    
    // If setting as background
    if (showBackgroundOptions === 'fromStock') {
      setBackgroundImage(imageUrl);
      setShowStockImages(false);
      setShowBackgroundOptions(false);
      toast.success('Background updated!');
      return;
    }
    
    // Adding new image
    const newElement = {
      id: Date.now(),
      type: 'image',
      x: 100,
      y: 100,
      w: 400,
      h: 400,
      zIndex: elements.length + 1,
      props: { src: imageUrl, opacity: 1 }
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveToHistory(newElements);
    setShowStockImages(false);
    toast.success('Image added to canvas');
  };
  
  const replaceImageFromUpload = (elementId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const removeBackground = window.confirm('Remove background from this image?');
        
        if (removeBackground) {
          setRemovingBackground(true);
          toast.info('Removing background...');
          
          try {
            const formData = new FormData();
            formData.append('image_file', file);
            formData.append('size', 'auto');
            
            const response = await axios.post(
              'https://api.remove.bg/v1.0/removebg',
              formData,
              {
                headers: { 'X-Api-Key': 'demo-key' },
                responseType: 'blob'
              }
            );
            
            const reader = new FileReader();
            reader.onloadend = () => {
              updateElementProps(elementId, { src: reader.result });
              setRemovingBackground(false);
              toast.success('Image replaced!');
            };
            reader.readAsDataURL(response.data);
          } catch (error) {
            console.error('Background removal failed:', error);
            toast.error('Background removal failed. Using original image.');
            setRemovingBackground(false);
            
            const reader = new FileReader();
            reader.onloadend = () => {
              updateElementProps(elementId, { src: reader.result });
              toast.success('Image replaced!');
            };
            reader.readAsDataURL(file);
          }
        } else {
          const reader = new FileReader();
          reader.onloadend = () => {
            updateElementProps(elementId, { src: reader.result });
            toast.success('Image replaced!');
          };
          reader.readAsDataURL(file);
        }
      }
    };
    input.click();
    setShowImageOptions(null);
  };
  
  const replaceBackgroundFromStock = () => {
    setShowBackgroundOptions('fromStock');
    searchStockImages('abstract background');
  };
  
  const replaceBackgroundFromUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setBackgroundImage(reader.result);
          toast.success('Background updated!');
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
    setShowBackgroundOptions(false);
  };
  
  const setBackgroundAsColor = () => {
    setBackgroundImage('');
    toast.info('Select a color from the background section');
    setShowBackgroundOptions(false);
  };

  const exportAsImage = async () => {
    if (canvasRef.current) {
      try {
        const canvas = await html2canvas(canvasRef.current, {
          backgroundColor: backgroundColor,
          scale: 2
        });
        
        const link = document.createElement('a');
        link.download = `${form.businessName || 'poster'}-${Date.now()}.png`;
        link.href = canvas.toDataURL();
        link.click();
        
        toast.success('Poster exported successfully!');
      } catch (error) {
        console.error('Export failed:', error);
        toast.error('Failed to export poster');
      }
    }
  };

  const selectedEl = elements.find(el => el.id === selectedElement);
  const size = CANVAS_SIZES[canvasSize];

  if (showEditor) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation />
        
        {/* Top Toolbar - Canva Style */}
        <div className="border-b border-gray-200 bg-white px-4 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-4">
            <Button onClick={closeEditor} variant="ghost" size="sm" className="hover:bg-gray-100">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <h1 className="text-lg font-semibold text-gray-800">Poster Editor</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <Button 
                onClick={undo} 
                disabled={historyIndex <= 0}
                variant="ghost" 
                size="sm"
                className="hover:bg-white disabled:opacity-40"
                title="Undo (Ctrl+Z)"
              >
                <Undo className="w-4 h-4" />
              </Button>
              <Button 
                onClick={redo} 
                disabled={historyIndex >= history.length - 1}
                variant="ghost" 
                size="sm"
                className="hover:bg-white disabled:opacity-40"
                title="Redo (Ctrl+Y)"
              >
                <Redo className="w-4 h-4" />
              </Button>
            </div>
            <Button onClick={exportAsImage} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Canva Style Icon Bar */}
          <div className="w-20 border-r border-gray-200 bg-white flex flex-col items-center py-4 gap-2">
            <button
              onClick={addTextElement}
              className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
              title="Add Text"
            >
              <Type className="w-6 h-6 text-gray-700" />
              <span className="text-xs text-gray-600">Text</span>
            </button>
            
            <button
              onClick={() => addShape('rectangle')}
              className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
              title="Add Rectangle"
            >
              <Square className="w-6 h-6 text-gray-700" />
              <span className="text-xs text-gray-600">Shape</span>
            </button>
            
            <button
              onClick={() => addShape('circle')}
              className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
              title="Add Circle"
            >
              <Circle className="w-6 h-6 text-gray-700" />
              <span className="text-xs text-gray-600">Circle</span>
            </button>
            
            <label className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer w-16">
              <Upload className="w-6 h-6 text-gray-700" />
              <span className="text-xs text-gray-600">Upload</span>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
            
            <button
              onClick={() => searchStockImages()}
              className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
              title="Search Stock Images"
            >
              <Search className="w-6 h-6 text-gray-700" />
              <span className="text-xs text-gray-600">Images</span>
            </button>
            
            <div className="my-4 h-px w-12 bg-gray-200" />
            
            <button
              onClick={() => {
                /* Toggle layers panel */
              }}
              className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-purple-50 transition-colors w-16"
              title="Layers"
            >
              <Layers className="w-6 h-6 text-gray-700" />
              <span className="text-xs text-gray-600">Layers</span>
              <span className="text-xs font-bold text-purple-600">{elements.length}</span>
            </button>
          </div>

          {/* Expanded Layers & Canvas Settings Panel */}
          <div className="w-64 border-r border-gray-200 bg-gray-50 overflow-y-auto">
            <div className="p-4 space-y-4">
              {/* Canvas Settings */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Canvas Size</h3>
                <Select value={canvasSize} onValueChange={setCanvasSize}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CANVAS_SIZES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Background Controls */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Background</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-600 mb-1 block">Color</label>
                    <Input
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="h-10 w-full cursor-pointer"
                    />
                  </div>
                  <Button
                    onClick={() => setShowBackgroundOptions(true)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <ImageIcon className="w-3 h-3 mr-2" />
                    Change Background
                  </Button>
                  {backgroundImage && (
                    <Button
                      onClick={() => {
                        setBackgroundImage('');
                        toast.success('Background removed');
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full text-red-600 hover:bg-red-50"
                    >
                      <X className="w-3 h-3 mr-2" />
                      Remove Image
                    </Button>
                  )}
                </div>
              </div>

              {/* Layers List */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Layers ({elements.length})
                </h3>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {elements.length === 0 ? (
                    <p className="text-xs text-gray-400 text-center py-8">No elements yet</p>
                  ) : (
                    [...elements].reverse().map((el) => (
                      <div
                        key={el.id}
                        onClick={() => setSelectedElement(el.id)}
                        className={`p-2 rounded-lg text-xs cursor-pointer flex items-center justify-between transition-colors ${
                          selectedElement === el.id 
                            ? 'bg-purple-100 border-2 border-purple-400' 
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1 truncate">
                          {el.type === 'text' && <Type className="w-3 h-3 text-gray-600" />}
                          {el.type === 'image' && <ImageIcon className="w-3 h-3 text-gray-600" />}
                          {el.type === 'rectangle' && <Square className="w-3 h-3 text-gray-600" />}
                          {el.type === 'circle' && <Circle className="w-3 h-3 text-gray-600" />}
                          <span className="truncate text-gray-700 font-medium">
                            {el.type === 'text' ? el.props.text.substring(0, 15) + '...' : el.type.charAt(0).toUpperCase() + el.type.slice(1)}
                          </span>
                        </div>
                        <div className="flex gap-0.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveLayerUp(el.id);
                            }}
                            className="p-1 hover:bg-purple-100 rounded transition-colors"
                            title="Move Up"
                          >
                            <ArrowUp className="w-3 h-3 text-gray-600" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveLayerDown(el.id);
                            }}
                            className="p-1 hover:bg-purple-100 rounded transition-colors"
                            title="Move Down"
                          >
                            <ArrowDown className="w-3 h-3 text-gray-600" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

            {/* Canvas Area - Canva Style */}
            <div className="flex-1 bg-gray-100 flex items-center justify-center p-8 overflow-auto">
              <div
                ref={canvasRef}
                style={{
                  width: size.width / 2,
                  height: size.height / 2,
                  backgroundColor: backgroundColor,
                  backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                  backgroundSize: 'cover',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                className="shadow-2xl rounded-sm"
              >
                {elements.map((element) => (
                  <Rnd
                    key={element.id}
                    size={{ width: element.w / 2, height: element.h / 2 }}
                    position={{ x: element.x / 2, y: element.y / 2 }}
                    onDragStop={(e, d) => updateElement(element.id, { x: d.x * 2, y: d.y * 2 })}
                    onResizeStop={(e, direction, ref, delta, position) => {
                      updateElement(element.id, {
                        w: parseInt(ref.style.width) * 2,
                        h: parseInt(ref.style.height) * 2,
                        x: position.x * 2,
                        y: position.y * 2
                      });
                    }}
                    bounds="parent"
                    style={{ zIndex: element.zIndex }}
                  >
                    <div
                      onClick={() => setSelectedElement(element.id)}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: selectedElement === element.id ? '2px solid #8b5cf6' : 'none',
                        cursor: 'move'
                      }}
                    >
                      {element.type === 'text' && (
                        <div
                          contentEditable
                          suppressContentEditableWarning
                          onBlur={(e) => updateElementProps(element.id, { text: e.target.innerText })}
                          style={{
                            width: '100%',
                            height: '100%',
                            fontSize: element.props.fontSize / 2,
                            fontFamily: element.props.fontFamily,
                            color: element.props.color,
                            fontWeight: element.props.fontWeight,
                            textAlign: element.props.textAlign,
                            backgroundColor: element.props.backgroundColor || 'transparent',
                            padding: '8px',
                            outline: 'none',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: element.props.textAlign === 'center' ? 'center' : element.props.textAlign === 'right' ? 'flex-end' : 'flex-start',
                            boxSizing: 'border-box'
                          }}
                        >
                          {element.props.text}
                        </div>
                      )}
                      {element.type === 'image' && (
                        <div
                          onClick={() => {
                            setSelectedElement(element.id);
                            setShowImageOptions(element.id);
                          }}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            cursor: 'pointer',
                            borderRadius: element.props.borderRadius ? `${element.props.borderRadius / 2}px` : '0',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                          title="Click to show options"
                        >
                          <img
                            src={element.props.src}
                            alt=""
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              opacity: element.props.opacity,
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      )}
                      {element.type === 'rectangle' && (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: element.props.color,
                            opacity: element.props.opacity,
                            borderRadius: element.props.borderRadius
                          }}
                        />
                      )}
                      {element.type === 'circle' && (
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundColor: element.props.color,
                            opacity: element.props.opacity,
                            borderRadius: '50%'
                          }}
                        />
                      )}
                    </div>
                  </Rnd>
                ))}
              </div>
            </div>

            {/* Right Properties Panel - Canva Style */}
            <div className="w-80 border-l border-gray-200 bg-white overflow-y-auto">
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-4">
                  {selectedEl ? 'Edit Element' : 'Properties'}
                </h3>
                <div className="space-y-4">
                  {selectedEl ? (
                    <>
                      {selectedEl.type === 'text' && (
                        <>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Font</label>
                            <Select
                              value={selectedEl.props.fontFamily}
                              onValueChange={(val) => {
                                loadGoogleFont(val);
                                updateElementProps(selectedEl.id, { fontFamily: val });
                              }}
                            >
                              <SelectTrigger className="bg-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-h-60 overflow-y-auto">
                                {GOOGLE_FONTS.map(font => (
                                  <SelectItem key={font} value={font} style={{ fontFamily: font }}>
                                    {font}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Size</label>
                            <Input
                              type="number"
                              value={selectedEl.props.fontSize}
                              onChange={(e) => updateElementProps(selectedEl.id, { fontSize: Number(e.target.value) })}
                              min="12"
                              max="200"
                              className="bg-white"
                            />
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Text Color</label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={selectedEl.props.color}
                                onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                                className="h-10 w-16 cursor-pointer"
                              />
                              <Input
                                type="text"
                                value={selectedEl.props.color}
                                onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                                className="flex-1 bg-white font-mono text-xs"
                              />
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Weight</label>
                            <Select
                              value={selectedEl.props.fontWeight}
                              onValueChange={(val) => updateElementProps(selectedEl.id, { fontWeight: val })}
                            >
                              <SelectTrigger className="bg-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="normal">Regular</SelectItem>
                                <SelectItem value="600">Semi-Bold</SelectItem>
                                <SelectItem value="bold">Bold</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Text Align</label>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={selectedEl.props.textAlign === 'left' ? 'default' : 'outline'}
                                onClick={() => updateElementProps(selectedEl.id, { textAlign: 'left' })}
                                className={selectedEl.props.textAlign === 'left' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                              >
                                <AlignLeft className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={selectedEl.props.textAlign === 'center' ? 'default' : 'outline'}
                                onClick={() => updateElementProps(selectedEl.id, { textAlign: 'center' })}
                                className={selectedEl.props.textAlign === 'center' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                              >
                                <AlignCenter className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={selectedEl.props.textAlign === 'right' ? 'default' : 'outline'}
                                onClick={() => updateElementProps(selectedEl.id, { textAlign: 'right' })}
                                className={selectedEl.props.textAlign === 'right' ? 'bg-purple-600 hover:bg-purple-700' : ''}
                              >
                                <AlignRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-3">Background</label>
                            <div className="space-y-3">
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">Color</label>
                                <Input
                                  type="color"
                                  value={selectedEl.props.backgroundColor || '#ffffff'}
                                  onChange={(e) => updateElementProps(selectedEl.id, { backgroundColor: e.target.value })}
                                  className="h-10 w-full cursor-pointer"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-gray-600 block mb-1">
                                  Opacity: {Math.round((selectedEl.props.backgroundOpacity || 1) * 100)}%
                                </label>
                                <Input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={selectedEl.props.backgroundOpacity || 1}
                                  onChange={(e) => updateElementProps(selectedEl.id, { backgroundOpacity: Number(e.target.value) })}
                                  className="w-full"
                                />
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full hover:bg-gray-100"
                                onClick={() => updateElementProps(selectedEl.id, { 
                                  backgroundColor: 'transparent',
                                  backgroundOpacity: 1
                                })}
                              >
                                Clear Background
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                      {selectedEl.type === 'image' && (
                        <>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Shape</label>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={!selectedEl.props.logoShape || selectedEl.props.logoShape === 'original' ? 'default' : 'outline'}
                                onClick={() => updateElementProps(selectedEl.id, { logoShape: 'original', borderRadius: 0 })}
                                className="flex-1"
                              >
                                <Square className="w-4 h-4 mr-1" />
                                Square
                              </Button>
                              <Button
                                size="sm"
                                variant={selectedEl.props.logoShape === 'circle' ? 'default' : 'outline'}
                                onClick={() => updateElementProps(selectedEl.id, { logoShape: 'circle', borderRadius: selectedEl.w / 2 })}
                                className="flex-1"
                              >
                                <Circle className="w-4 h-4 mr-1" />
                                Circle
                              </Button>
                            </div>
                          </div>
                          
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">
                              Opacity: {Math.round((selectedEl.props.opacity || 1) * 100)}%
                            </label>
                            <Input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={selectedEl.props.opacity || 1}
                              onChange={(e) => updateElementProps(selectedEl.id, { opacity: Number(e.target.value) })}
                              className="w-full"
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full hover:bg-purple-50"
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/*';
                              input.onchange = (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    updateElementProps(selectedEl.id, { src: reader.result });
                                    toast.success('Image replaced');
                                  };
                                  reader.readAsDataURL(file);
                                }
                              };
                              input.click();
                            }}
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Replace Image
                          </Button>
                          <p className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                            💡 Tip: Double-click image on canvas to replace
                          </p>
                        </>
                      )}
                      {(selectedEl.type === 'rectangle' || selectedEl.type === 'circle') && (
                        <>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">Fill Color</label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={selectedEl.props.color}
                                onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                                className="h-10 w-16 cursor-pointer"
                              />
                              <Input
                                type="text"
                                value={selectedEl.props.color}
                                onChange={(e) => updateElementProps(selectedEl.id, { color: e.target.value })}
                                className="flex-1 bg-white font-mono text-xs"
                              />
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <label className="text-xs font-medium text-gray-700 block mb-2">
                              Opacity: {Math.round((selectedEl.props.opacity || 1) * 100)}%
                            </label>
                            <Input
                              type="range"
                              min="0"
                              max="1"
                              step="0.1"
                              value={selectedEl.props.opacity || 1}
                              onChange={(e) => updateElementProps(selectedEl.id, { opacity: Number(e.target.value) })}
                              className="w-full"
                            />
                          </div>
                        </>
                      )}
                      
                      <div className="pt-4 border-t border-gray-200 space-y-2">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <label className="text-xs font-medium text-gray-700 block mb-2">Layer Order</label>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => moveLayerUp(selectedEl.id)}
                              variant="outline"
                              size="sm"
                              className="flex-1 hover:bg-purple-50"
                              title="Move Forward"
                            >
                              <ArrowUp className="w-4 h-4 mr-1" />
                              Forward
                            </Button>
                            <Button
                              onClick={() => moveLayerDown(selectedEl.id)}
                              variant="outline"
                              size="sm"
                              className="flex-1 hover:bg-purple-50"
                              title="Move Backward"
                            >
                              <ArrowDown className="w-4 h-4 mr-1" />
                              Back
                            </Button>
                          </div>
                        </div>
                        <Button
                          onClick={() => duplicateElement(selectedEl.id)}
                          variant="outline"
                          size="sm"
                          className="w-full hover:bg-purple-50"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </Button>
                        <Button
                          onClick={() => deleteElement(selectedEl.id)}
                          variant="outline"
                          size="sm"
                          className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Element
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">Select an element to edit</p>
                      <p className="text-xs mt-1">Click any element on the canvas</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Stock Images Modal - Canva Style */}
          {showStockImages && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
              <div className="bg-white rounded-xl max-w-6xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
                <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-white">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">Stock Images</h3>
                    <p className="text-sm text-gray-600">Powered by Pexels</p>
                  </div>
                  <Button 
                    onClick={() => setShowStockImages(false)} 
                    variant="ghost" 
                    size="sm"
                    className="hover:bg-gray-100 rounded-full w-10 h-10 p-0"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="p-6">
                  <div className="flex gap-3 mb-6">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        placeholder="Search for images... (e.g., 'business', 'nature', 'office')"
                        className="pl-10 h-12 text-base bg-gray-50 border-gray-200 focus:bg-white"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            searchStockImages(e.target.value);
                          }
                        }}
                      />
                    </div>
                    <Button 
                      onClick={() => {
                        const input = document.querySelector('input[placeholder*="Search for images"]');
                        searchStockImages(input.value);
                      }}
                      className="h-12 px-6 bg-purple-600 hover:bg-purple-700"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      Search
                    </Button>
                  </div>
                  
                  {loadingStock ? (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600 mb-4"></div>
                      <p className="text-gray-600">Finding perfect images...</p>
                    </div>
                  ) : stockImages.length > 0 ? (
                    <div className="overflow-y-auto max-h-[500px] pr-2">
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {stockImages.map(img => (
                          <div
                            key={img.id}
                            onClick={() => addStockImageToCanvas(img.url)}
                            className="group cursor-pointer rounded-lg overflow-hidden border-2 border-transparent hover:border-purple-500 transition-all duration-200 hover:shadow-lg hover:scale-105"
                          >
                            <div className="relative">
                              <img
                                src={img.thumbnail}
                                alt=""
                                className="w-full h-40 object-cover"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                                <Plus className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                              </div>
                            </div>
                            {img.photographer && (
                              <div className="p-2 bg-gray-50 text-xs text-gray-600 truncate">
                                by {img.photographer}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-16 text-gray-400">
                      <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium text-gray-600 mb-2">Search for stock images</p>
                      <p className="text-sm">Enter a keyword and press Enter or click Search</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
      </div>
    );
  }

  // Main Strategy Page (when editor is not shown)
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Digi Strategy</h1>
              <p className="text-gray-600">AI-powered business strategy & social media marketing</p>
            </div>
          </div>
          
          {hasProfile && (
            <div className="flex items-center gap-2">
              {isPro ? (
                <div className="flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-lg">
                  <Crown className="w-5 h-5" />
                  <span className="font-semibold">Pro Member</span>
                </div>
              ) : (
                <Button onClick={handleUpgradeToPro} variant="outline" className="flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  Upgrade to Pro
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Business Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-600" />
              Business Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Business Name *</label>
                <Input
                  placeholder="DigiDream Solutions"
                  value={form.businessName}
                  onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Owner/Contact Name *</label>
                <Input
                  placeholder="John Smith"
                  value={form.ownerName}
                  onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Business Type *</label>
                <Select value={form.businessType} onValueChange={(value) => setForm({ ...form, businessType: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Business Email *</label>
                <Input
                  type="email"
                  placeholder="contact@business.com"
                  value={form.businessEmail}
                  onChange={(e) => setForm({ ...form, businessEmail: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Location *</label>
                <Input
                  placeholder="City, State"
                  value={form.businessLocation}
                  onChange={(e) => setForm({ ...form, businessLocation: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Phone Number</label>
                <Input
                  placeholder="+1 (555) 123-4567"
                  value={form.businessPhone}
                  onChange={(e) => setForm({ ...form, businessPhone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Business Description *</label>
              <Textarea
                placeholder="Describe your business, services, target market..."
                value={form.businessDescription}
                onChange={(e) => setForm({ ...form, businessDescription: e.target.value })}
                rows={4}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Business Logo</label>
              <div className="flex items-center gap-3">
                {form.logoUrl && (
                  <img src={form.logoUrl} alt="Logo" className="w-16 h-16 object-contain border rounded" />
                )}
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg border">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">{form.logoUrl ? 'Change Logo' : 'Upload Logo'}</span>
                  </div>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
              size="lg"
            >
              {generating ? (
                <>
                  <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                  Generating Strategy...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate AI Strategy & Marketing Plan
                </>
              )}
            </Button>

            {hasProfile && (
              <div className="text-center text-sm text-gray-600 mt-2">
                Generations used: {generationCount} {!isPro && `| ${Math.max(0, 2 - generationCount)} free left`}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Generated Strategy */}
        {strategy && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                  Business Improvement Strategy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">{strategy.strategy}</pre>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-600" />
                  Marketing Plan
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700">{strategy.marketingPlan}</pre>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <Button
                  onClick={loadMyDesigns}
                  variant="outline"
                  className="flex items-center gap-2 hover:bg-purple-50"
                >
                  <Layers className="w-4 h-4" />
                  My Designs
                </Button>
                <Button
                  onClick={() => setShowTemplates(true)}
                  variant="outline"
                  className="flex items-center gap-2 hover:bg-purple-50"
                >
                  <Target className="w-4 h-4" />
                  Templates
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-orange-600" />
                  Social Media Posts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {strategy.socialPosts && strategy.socialPosts.map((post) => (
                  <div key={post.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <span className="text-xs font-medium text-orange-600 uppercase">{post.type}</span>
                        <p className="mt-2 text-sm text-gray-700">{post.content}</p>
                      </div>
                      <Button
                        onClick={() => openEditor(post)}
                        size="sm"
                        className="flex items-center gap-2 bg-gradient-to-r from-orange-600 to-red-600"
                      >
                        <ImageIcon className="w-4 h-4" />
                        Create Poster
                      </Button>
                    </div>
                  </div>
                ))}
                
                <div className="pt-4 border-t">
                  <Button
                    onClick={() => openEditor()}
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600"
                  >
                    <ImageIcon className="w-5 h-5 mr-2" />
                    Create Custom Poster
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
        
        {/* My Designs Modal */}
        {showMyDesigns && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">My Designs</h3>
                  <p className="text-sm text-gray-600">Load and edit your saved designs</p>
                </div>
                <Button onClick={() => setShowMyDesigns(false)} variant="ghost" size="sm">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
                {myDesigns.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Layers className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium text-gray-600 mb-2">No saved designs yet</p>
                    <p className="text-sm">Create a poster and it will be auto-saved here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    {myDesigns.map(design => (
                      <div key={design.id} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        <div className="aspect-square bg-gray-100 flex items-center justify-center p-4">
                          <div className="text-center">
                            <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                            <p className="text-xs text-gray-600">{design.canvasSize}</p>
                          </div>
                        </div>
                        <div className="p-3 space-y-2">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {new Date(design.createdAt).toLocaleDateString()}
                          </p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => loadDesign(design)}
                              size="sm"
                              className="flex-1 bg-purple-600 hover:bg-purple-700"
                            >
                              Load
                            </Button>
                            <Button
                              onClick={() => deleteDesign(design.id)}
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Templates Modal */}
        {showTemplates && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-5xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Pre-made Templates</h3>
                  <p className="text-sm text-gray-600">Choose a template to customize</p>
                </div>
                <Button onClick={() => setShowTemplates(false)} variant="ghost" size="sm">
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
                <div className="grid grid-cols-3 gap-4">
                  {TEMPLATE_CATEGORIES.map(template => (
                    <div 
                      key={template.id}
                      onClick={() => loadTemplate(template)}
                      className="border-2 rounded-lg overflow-hidden hover:border-purple-500 transition-all cursor-pointer group"
                    >
                      <div className="aspect-square bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center p-6">
                        <div className="text-center">
                          <Target className="w-16 h-16 mx-auto mb-3 text-purple-600" />
                          <p className="text-lg font-bold text-purple-900">{template.name}</p>
                        </div>
                      </div>
                      <div className="p-3 bg-gray-50 group-hover:bg-purple-50 transition-colors">
                        <p className="text-sm font-medium text-gray-700 text-center">Click to use</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Image Options Context Menu */}
        {showImageOptions && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={() => setShowImageOptions(null)}>
            <div className="bg-white rounded-xl shadow-2xl p-4 w-72" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Replace Image</h3>
              <div className="space-y-2">
                <Button
                  onClick={() => {
                    searchStockImages();
                  }}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start hover:bg-purple-50"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Replace from Stock Images
                </Button>
                <Button
                  onClick={() => replaceImageFromUpload(showImageOptions)}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start hover:bg-purple-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Replace from Upload
                </Button>
                <Button
                  onClick={() => setShowImageOptions(null)}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Background Options Modal */}
        {showBackgroundOptions && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={() => setShowBackgroundOptions(false)}>
            <div className="bg-white rounded-xl shadow-2xl p-4 w-72" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Change Background</h3>
              <div className="space-y-2">
                <Button
                  onClick={replaceBackgroundFromStock}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start hover:bg-purple-50"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Choose from Stock Images
                </Button>
                <Button
                  onClick={replaceBackgroundFromUpload}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start hover:bg-purple-50"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload from Gallery
                </Button>
                <Button
                  onClick={setBackgroundAsColor}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start hover:bg-purple-50"
                >
                  <Circle className="w-4 h-4 mr-2" />
                  Use Solid Color
                </Button>
                <Button
                  onClick={() => setShowBackgroundOptions(false)}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
